import React, { useEffect, useRef, useState } from 'react';
import { animated, useSprings } from '@react-spring/web';

interface SplitTextProps {
  text: string;
  className?: string;
  delay?: number;
  threshold?: number;
  rootMargin?: string;
  textAlign?: React.CSSProperties['textAlign'];
  easing?: string;
  onLetterAnimationComplete?: () => void;
  animationFrom?: {
    opacity?: number;
    transform?: string;
    // Add any other numeric/string fields you need
  };
  animationTo?: {
    opacity?: number;
    transform?: string;
  };
}

/**
 * Splits text into letters and animates each letter from
 * { opacity: 0, transform: 'translate3d(0,40px,0)' }
 * to
 * { opacity: 1, transform: 'translate3d(0,0,0)' }
 * once the paragraph is in view.
 */
const SplitText: React.FC<SplitTextProps> = ({
  text,
  className,
  delay = 60,
  threshold = 0.1,
  rootMargin = '-100px',
  textAlign = 'center',
}) => {
  const letters = text.split('');
  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (ref.current) observer.unobserve(ref.current);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  // We only define two style fields: opacity & transform.
  // This is key to avoiding the "indexed property" error.
  const springs = useSprings(
    letters.length,
    letters.map((_, index) => ({
      from: {
        opacity: 0,
        transform: 'translate3d(0,40px,0)',
      },
      to: inView
        ? {
            opacity: 1,
            transform: 'translate3d(0,0,0)',
          }
        : {
            opacity: 0,
            transform: 'translate3d(0,40px,0)',
          },
      delay: index * delay,
      config: { tension: 150, friction: 18 },
    }))
  );

  return (
    <p
      ref={ref}
      className={className}
      style={{
        textAlign,
        display: 'inline-block',
        overflow: 'hidden',
      }}
    >
      {springs.map(({ opacity, transform }, i) => (
        // We destructure only "opacity" and "transform"
        // so no extra numeric or filter fields get passed.
        <animated.span
          key={i}
          style={{
            display: 'inline-block',
            willChange: 'opacity, transform',
            opacity,
            transform,
          }}
        >
          {letters[i] === ' ' ? '\u00A0' : letters[i]}
        </animated.span>
      ))}
    </p>
  );
};

export default SplitText;